
import React, { useState, useMemo, useRef } from 'react';

const SpeechReader = () => {
  const [speed, setSpeed] = useState(0.7);
  const [pause, setPause] = useState(4);
  const [selectedStoryId, setSelectedStoryId] = useState('custom');
  const [text, setText] = useState(
    'Click "Random Story" or pick a one‑page story, or type your own.'
  );

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const chunksRef = useRef([]);
  const indexRef = useRef(0);
  const timeoutRef = useRef(null);
  const currentUtteranceRef = useRef(null);

  // One-page stories
  const stories = useMemo(() => [
    {
      id: 'starlight-library',
      title: 'The Starlight Library',
      content: `Every clear evening a thin ladder of pale silver light touched the meadow behind Nira's cottage. Most people never noticed it. Nira did. One night she carried a crust of bread, a pencil, and her strongest curiosity, then placed a careful foot on the lowest glowing rung. It held. She climbed through drifting moths and a pocket of soft, warm wind until she reached a floating porch made of quiet shimmer. A door of slow-turning constellations opened and a calm voice whispered, "Welcome reader." Shelves curved in spiral arcs, holding books grown from glimmering crystal bark. Their titles rearranged themselves to match the thoughts passing through her mind: How Seeds Travel, The History of Forgotten Rainbows, A Guide to Listening to Stones. Nira chose a thin volume that hummed like a kettle about to whistle. When she opened it, letters rose like dandelion fluff, drifting into shapes she could touch. Each sentence became a small scene: a seed hitching a ride on a fox tail, a raindrop teaching a root to be patient, a hill remembering the ocean it once had been. Hours felt like gentle minutes. Nira worried about climbing down in darkness, but the librarian (a being shaped like a folded map of night) handed her a bookmark glowing with dependable gold. "Lay this on the grass whenever your questions grow wider than the sky," it said. Back in the meadow the ladder folded into a single star and winked out. Nira kept the bookmark near her window. She did not tell anyone at first. Instead she began answering little mysteries around town: why the bakery yeast slept in the cold corner, where lost marbles rolled, when the swifts would return. People asked how she knew. Nira only smiled and said, "I read carefully." And every time her questions grew larger than her courage, she placed the bookmark on the grass and climbed again.`
    },
    {
      id: 'clockwork-garden',
      title: 'The Clockwork Garden',
      content: `Jalen lived beside a garden that would not grow. The soil was pale, the air a bit too dry, and seeds gave up before they even cracked. One afternoon he found a small brass gear half-buried near the fence, warm to the touch though the sun had slipped behind clouds. When he pressed its center, a soft ticking answered from below the ground. He dug gently and uncovered a circular hatch disguised as a patch of stubborn clay. Inside, a chamber of intertwined copper vines ticked and breathed like a resting creature. A diagram lay on a pedestal: Plant the gears, water with patient rhythm, listen for alignment. Confused but hopeful, Jalen searched the yard and discovered more tiny gears hidden under pebbles, in knot holes, and inside an abandoned birdhouse. He planted each where rows should have been. That evening he filled a dented watering can and dripped water in a steady beat: pour, pause, pour, pause. The ground answered with faint chimes, notes tightening into harmony. By morning delicate stems of brushed metal had risen, unfurling leaves made of green glass veined with liquid mercury. Tiny buds opened into blossoms that exhaled cool mist, adding gentle moisture to the air. Real seeds—ones he had abandoned—sprouted beside the mechanical stems, grateful for shade and balance. Pollinating beetles arrived, polishing their legs on brass petals, carrying real pollen to real blooms. Jalen realized the garden wanted partnership, not force. He built a small wind scoop that turned a fan which kept the subterranean mechanism from overheating. The clockwork slowed at night, letting fireflies rest on motionless gears. When neighbors peered over the fence, he invited them to plant wishes written on seed paper between the metal roots. Most sprouted. Some waited. A few returned later in surprising shapes. The garden never became ordinary. It became something better—a place where patient rhythm, small found things, and ordinary water taught a boy that growth can be engineered only up to the edge of life, and after that, kindness finishes the design.`
    },
    {
      id: 'river-of-whispers',
      title: 'River of Whispers',
      content: `In Mara's valley the river never roared or babbled. It whispered. If you knelt at the bank and leaned close, the current shaped words around smooth stones. Most villagers hurried across the wooden bridge, too busy to listen. Mara was not. Each dawn she carried a basket of chalk fragments and questions. She would ask one, then arrange three pebbles. The river would curl a faint answer: Wait two rains. Or: Ask the herons. Or sometimes: Trade your silence for its story. One dry season the whisper thinned to raspy threads. Fish lingered in deep pockets, dragonflies hovered uncertainly, and the millwheel slowed to a sulk. Mara pressed her ear to the bank. The river struggled to form even a syllable. She followed the upstream path, past sleeping reeds and bent thistles, until she reached a collapsed hollow where an old beaver dam had crumbled inward, trapping a knot of driftwood, fabric scraps, and a rusted cooking pan. Water squeezed through, exhausted. Mara spent the afternoon prying loose the jam. She sorted branches into rebuilding piles, stacked stones to guide the current, and carried the metal debris away. When the last plank of blockage shifted, a bright rush surged forward, wrapping her ankles in grateful coolness. That night the river regained its gentle consonants. It offered her a new sound—one she had never heard—a low melodic promise: We remember helpers. The next week seedlings near the restored bank sprouted leaves shaped like little cupped ears. Villagers finally noticed, marveling at the listening plants. Mara taught them how to kneel without splashing, how to quiet the clatter inside their heads, how to hear directions like Turn soil after the lavender or Share buckets during noon heat. The harvest that year was balanced instead of abundant: enough berries, enough grain, enough patience. The river still whispered secrets, but now it also received them. People left thanks, hopes, and sometimes apologies on flat stones. The current carried each offering away, weaving a language of shared care that Mara could feel in the soles of her feet whenever she crossed the bridge.`
    },
    {
      id: 'balloon-cartographer',
      title: 'The Balloon Cartographer',
      content: `Omi mapped places most people could not point to: drafts inside attics, scents drifting between market stalls, and pockets of hush in noisy streets. His tool was a cluster of listening balloons—thin, translucent spheres tethered to a belt spooled with careful knots. Each balloon held a faint swirling tint: blue for breezes, gold for warmth, violet for echoes. On the first day of the long fair, a muddle of smells, clanks, fiddles, and shouting tangled above the square until stall awnings trembled. Birds gave up mid-song. Omi released a blue balloon. It rose, quivered, then drifted along an invisible corridor of cooler air. He followed, winding through crates until he found a narrow gap between two booths where heat and noise slammed together. He propped a reflective cloth to redirect roasting heat, then chalked a tiny swirl on a paving stone: Vent here. He sent a violet balloon next. It bobbed, absorbed stray echoes, and guided him to a spot beneath the bell tower where overlapping fiddle tunes merged into headache fuzz. He hung a line of small felt flags. They broke the echo into soft feathers of sound that settled instead of colliding. By afternoon people noticed the square felt... breathable. Musicians could hear their own tempo. The baker's sweet steam no longer smothered the spice seller's pepper crackle. A traveling astronomer, puzzled by the improvement, asked Omi for a map. That night, under lantern light, he inked a parchment showing arches of scent, valleys of cool, reefs of echo, and isles of quiet. The legend read: Adjust, Do Not Conquer. The astronomer promised to share the method in distant towns. Before packing up, Omi released one final clear balloon. It carried a personal question upward: Where next? Dawn wind brought it back, ribbon wrapped around a weather vane pointing toward the harbor. Omi smiled, retied his knots, and set off to map the unspoken drafts between sails, ready to keep treating air as something you live inside, not just something you breathe past.`
    }
  ], []);

  const pickRandomStory = () => {
    const idx = Math.floor(Math.random() * stories.length);
    const story = stories[idx];
    setSelectedStoryId(story.id);
    setText(story.content);
  };

  const handleSelectChange = (e) => {
    const id = e.target.value;
    setSelectedStoryId(id);
    if (id === 'custom') return;
    const story = stories.find(s => s.id === id);
    if (story) setText(story.content);
  };

  // Chunk builder: pause every 3 words or at sentence period
  const buildChunks = (input) => {
    const chunks = [];
    let buffer = [];
    const tokens = input.split(/\s+/);
    tokens.forEach(raw => {
      if (!raw) return;
      let word = raw;
      let trailingPeriods = '';
      while (word.endsWith('.')) {
        trailingPeriods = '.' + trailingPeriods;
        word = word.slice(0, -1);
      }
      if (word) buffer.push(word + trailingPeriods);
      else if (trailingPeriods && buffer.length) {
        buffer[buffer.length - 1] += trailingPeriods;
      }
      const sentenceEnded = trailingPeriods.length > 0;
      const threeWordsReached = buffer.length === 3;
      if (sentenceEnded || threeWordsReached) {
        chunks.push(buffer.join(' '));
        buffer = [];
      }
    });
    if (buffer.length) chunks.push(buffer.join(' '));
    return chunks;
  };

  const clearPending = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const speakNext = () => {
    if (isPaused) return; // Don't start new chunk if paused
    const chunks = chunksRef.current;
    if (indexRef.current >= chunks.length) {
      setIsSpeaking(false);
      setIsPaused(false);
      return;
    }
    const textChunk = chunks[indexRef.current];
    const utterance = new window.SpeechSynthesisUtterance(textChunk);
    currentUtteranceRef.current = utterance;
    utterance.lang = 'en-US';
    utterance.rate = speed;
    utterance.onend = () => {
      if (isPaused) return; // Don't continue if paused
      indexRef.current += 1;
      clearPending();
      timeoutRef.current = setTimeout(() => {
        speakNext();
      }, pause * 1000);
    };
    window.speechSynthesis.speak(utterance);
  };

  const handleRead = () => {
    if (!('speechSynthesis' in window)) {
      alert('Sorry, your browser does not support speech synthesis.');
      return;
    }
    window.speechSynthesis.cancel();
    clearPending();
    chunksRef.current = buildChunks(text);
    indexRef.current = 0;
    setIsSpeaking(true);
    setIsPaused(false);
    speakNext();
  };

  const handleTogglePause = () => {
    if (!isSpeaking) return;
    if (!isPaused) {
      setIsPaused(true);
      clearPending();
      try { window.speechSynthesis.pause(); } catch (_) {}
    } else {
      setIsPaused(false);
      try { window.speechSynthesis.resume(); } catch (_) {}
      // If not currently speaking, start next chunk
      if (!window.speechSynthesis.speaking && indexRef.current < chunksRef.current.length) {
        clearPending();
        speakNext();
      }
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '2rem auto', fontFamily: 'sans-serif', lineHeight: 1.45 }}>
      <h2 style={{ marginTop: 0 }}>Story Reader for Writing</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select
          value={selectedStoryId}
          onChange={handleSelectChange}
          style={{ flex: '1 1 320px', padding: 6 }}
        >
          <option value="custom">Custom (editable)</option>
          {stories.map(st => (
            <option key={st.id} value={st.id}>{st.title}</option>
          ))}
        </select>
        <button onClick={pickRandomStory} style={{ padding: '6px 12px' }}>
          Random Story
        </button>
      </div>

      <textarea
        value={text}
        onChange={e => {
          setText(e.target.value);
          if (selectedStoryId !== 'custom') setSelectedStoryId('custom');
        }}
        rows={18}
        style={{ width: '100%', fontSize: 15, marginBottom: 16 }}
        placeholder="Story will appear here..."
      />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <label style={{ flex: '1 1 240px' }}>
          Speed: {speed.toFixed(2)}
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.01"
            value={speed}
            onChange={e => setSpeed(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </label>
        <label style={{ flex: '1 1 160px' }}>
          Pause (s):
          <input
            type="number"
            min="0"
            value={pause}
            onChange={e => setPause(Number(e.target.value))}
            style={{ width: '100%', marginTop: 4 }}
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleRead} style={{ padding: '10px 18px', fontSize: 16 }}>
          Read Aloud
        </button>
        <button
          onClick={handleTogglePause}
            disabled={!isSpeaking}
          style={{ padding: '10px 18px', fontSize: 16 }}
        >
          {isPaused ? 'Continue' : 'Pause'}
        </button>
      </div>
    </div>
  );
};

export default SpeechReader;